// The Plan header (wireframe 2): ‹ week › with "This week / Sep 28 to Oct 4",
// and the week menu (Q11 of the flows doc: name, people served, print,
// share, publish, clear -- plus other weeks and jumping to a date, which used
// to be lists and controls on the page itself). A menu choice that needs
// more than one tap opens a panel under the header; sharing and publishing
// open the plan share sheet (slice 8b).
import { useState } from "react";
import { useMealPlanner } from "../../hooks/MealPlannerContext";
import { MoreMenu, type MoreMenuItem } from "../MoreMenu";
import { PlanShareSheet } from "./PlanShareSheet";
import { COLORS, FONT_SIZE, SPACE } from "../../lib/constants";
import { currentWeekStart, getWeekStartDate, orderedDays, shiftWeek, toISODate } from "../../lib/utils";
import { weekMealCount } from "../../lib/planUtils";
import { weekLabel, weekRange, weekSummary } from "../../lib/planView";
import type { Day } from "../../lib/types";
import { inputStyle, panel, roundButton, textButton } from "./planStyles";
import { supporting } from "../recipeStyles";

type Panel = null | "name" | "people" | "share" | "publish" | "weeks";

export function WeekHeader() {
  const mp = useMealPlanner();
  const [open, setOpen] = useState<Panel>(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const thisWeek = currentWeekStart(mp.planDefaults.weekStartDay);
  const hasMeals = weekMealCount(mp.weekData) > 0;
  const toggle = (p: Panel) => setOpen((cur) => (cur === p ? null : p));
  const goTo = (weekStart: string) => {
    mp.setWeekStart(weekStart);
    setOpen(null);
    setConfirmClear(false);
  };

  const items: MoreMenuItem[] = [
    { label: mp.weekData.title ? "Rename this week" : "Name this week", onSelect: () => toggle("name") },
    { label: `People served: ${mp.weekData.servings || 4}`, onSelect: () => toggle("people") },
    { label: "Other weeks and dates", onSelect: () => toggle("weeks") },
    { label: "Print", onSelect: () => window.print() },
    { label: "Share this week", onSelect: () => setOpen("share") },
    { label: "Publish as a reusable plan", onSelect: () => setOpen("publish") },
  ];
  // Clearing is for this week and later, and only when there's something to clear.
  if (hasMeals && mp.weekStart >= thisWeek) {
    items.push(
      confirmClear
        ? { label: "Clear it, for good", danger: true, onSelect: () => (mp.clearCurrentWeek(), setConfirmClear(false)) }
        : { label: "Clear this week", danger: true, keepOpen: true, onSelect: () => setConfirmClear(true) }
    );
  }

  return (
    <div className="mp-sans" style={{ display: "flex", flexDirection: "column", gap: SPACE.lg, marginBottom: SPACE.x3 }}>
      <div style={{ display: "flex", alignItems: "center", gap: SPACE.md }}>
        <button type="button" aria-label="Previous week" onClick={() => goTo(shiftWeek(mp.weekStart, -1))} style={roundButton}>
          ‹
        </button>
        <div style={{ flex: 1, textAlign: "center", minWidth: 0 }}>
          <div style={supporting}>{weekLabel(mp.weekStart, thisWeek)}</div>
          <div style={{ fontWeight: 700, fontSize: FONT_SIZE.lg }}>{weekRange(mp.weekStart)}</div>
        </div>
        <button type="button" aria-label="Next week" onClick={() => goTo(shiftWeek(mp.weekStart, 1))} style={roundButton}>
          ›
        </button>
        <MoreMenu label="Week menu: name, people served, other weeks, print, share, publish, clear" items={items} onClose={() => setConfirmClear(false)} />
      </div>
      {mp.weekStart !== thisWeek && (
        <button type="button" onClick={() => goTo(thisWeek)} style={{ ...textButton, alignSelf: "center", fontSize: FONT_SIZE.sm }}>
          Back to this week
        </button>
      )}

      {open === "name" && (
        <div style={panel}>
          <label style={{ ...supporting, display: "block", marginBottom: SPACE.sm }} htmlFor="week-title">
            A name for this week, if you like ("Beach trip", "Back to school")
          </label>
          <input
            id="week-title"
            autoFocus
            value={mp.weekData.title || ""}
            onChange={(e) => mp.updateCurrentWeek((wd) => ({ ...wd, title: e.target.value }))}
            onKeyDown={(e) => e.key === "Enter" && setOpen(null)}
            style={{ ...inputStyle, width: "100%" }}
          />
        </div>
      )}

      {open === "people" && (
        <div style={{ ...panel, display: "flex", alignItems: "center", justifyContent: "space-between", gap: SPACE.xl, flexWrap: "wrap" }}>
          <span>
            <strong>People served this week</strong>
            <span style={{ ...supporting, display: "block" }}>Recipes are scaled up to this, plus any leftovers they cover.</span>
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: SPACE.md }}>
            <button type="button" aria-label="Fewer people" style={roundButton} onClick={() => mp.updateCurrentWeek((wd) => ({ ...wd, servings: Math.max(1, (wd.servings || 4) - 1) }))}>
              −
            </button>
            <strong aria-live="polite" style={{ minWidth: 24, textAlign: "center" }}>
              {mp.weekData.servings || 4}
            </strong>
            <button type="button" aria-label="More people" style={roundButton} onClick={() => mp.updateCurrentWeek((wd) => ({ ...wd, servings: (wd.servings || 4) + 1 }))}>
              +
            </button>
          </span>
        </div>
      )}

      {open === "weeks" && <OtherWeeks onOpen={goTo} />}
      {(open === "share" || open === "publish") && <PlanShareSheet initialDay={null} openPublish={open === "publish"} onClose={() => setOpen(null)} />}
    </div>
  );
}

// Every other week with meals, upcoming first then past, and a date picker
// for anything else (feedback #28, #29).
function OtherWeeks({ onOpen }: { onOpen: (weekStart: string) => void }) {
  const mp = useMealPlanner();
  const thisWeek = currentWeekStart(mp.planDefaults.weekStartDay);
  const days = orderedDays(mp.planDefaults.weekStartDay) as Day[];
  const others = Object.keys(mp.plansByWeek).filter((k) => k !== mp.weekStart && weekMealCount(mp.plansByWeek[k]) > 0);
  const upcoming = others.filter((k) => k >= thisWeek).sort();
  const past = others.filter((k) => k < thisWeek).sort((a, b) => (a < b ? 1 : -1));
  const row = (k: string) => (
    <button
      key={k}
      type="button"
      onClick={() => onOpen(k)}
      style={{ ...textButton, color: COLORS.ink, fontWeight: 400, width: "100%", justifyContent: "space-between", borderBottom: `1px solid ${COLORS.line}`, textAlign: "left" }}
    >
      <span>
        <strong>{mp.plansByWeek[k].title || weekRange(k)}</strong>
        <span style={{ ...supporting, display: "block" }}>
          {mp.plansByWeek[k].title ? `${weekRange(k)} · ` : ""}
          {weekSummary(mp.plansByWeek[k], days)}
        </span>
      </span>
      <span aria-hidden="true" style={{ color: COLORS.accent }}>
        ›
      </span>
    </button>
  );
  return (
    <div style={panel}>
      <label style={{ ...supporting, display: "flex", alignItems: "center", gap: SPACE.md, flexWrap: "wrap" }}>
        Go to the week of
        <input
          type="date"
          value={mp.weekStart}
          onChange={(e) => e.target.value && onOpen(toISODate(getWeekStartDate(new Date(`${e.target.value}T00:00:00`), mp.planDefaults.weekStartDay)))}
          style={inputStyle}
        />
      </label>
      {upcoming.length > 0 && (
        <>
          <div style={{ ...supporting, fontWeight: 700, marginTop: SPACE.xl }}>Coming up</div>
          {upcoming.map(row)}
        </>
      )}
      <div style={{ ...supporting, fontWeight: 700, marginTop: SPACE.xl }}>Earlier</div>
      {past.length > 0 ? past.map(row) : <p style={{ ...supporting, margin: 0 }}>No earlier weeks with meals yet.</p>}
    </div>
  );
}
